import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import { FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import mlogo from "../images/logo/mlogo.png";
import { useTranslation } from "react-i18next";
import { TbBrandYoutube } from "react-icons/tb";
import i18next from "i18next";
import cookies from "js-cookie";
import { GiHamburgerMenu } from "react-icons/gi";
import { FiFacebook, FiInstagram, FiLinkedin } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import { WiDirectionRight } from "react-icons/wi";
import { IoMdHome } from "react-icons/io";
import { BsTwitterX } from "react-icons/bs";
import { GrServices, GrProjects } from "react-icons/gr";
import { GoFileMedia } from "react-icons/go";
import { TfiGallery } from "react-icons/tfi";
import { RiContactsBookLine } from "react-icons/ri";
import { TbArrowRoundaboutRight } from "react-icons/tb";
import { FaSearch } from "react-icons/fa";
import { useSite } from "../lib/SiteContext";
import QuoteModal from "./QuoteModal";
import SearchResults from "./SearchResults";

/** Icon names stored with a menu item map onto these components. */
const ICONS = {
  IoMdHome,
  TbArrowRoundaboutRight,
  GrServices,
  GrProjects,
  GoFileMedia,
  TfiGallery,
  RiContactsBookLine,
};

/**
 * The navigation the site ships with. It is used until the backend answers,
 * and whenever the backend is unavailable, so the header is never empty.
 */
const FALLBACK_MENU = [
  { translation_key: "header.first-menu", url: "/", icon: "IoMdHome", children: [] },
  {
    translation_key: "header.second-menu",
    url: "/about/history",
    icon: "TbArrowRoundaboutRight",
    children: [
      { translation_key: "header.second-sub-menu-1", url: "/about/history" },
      { translation_key: "header.second-sub-menu-2", url: "/about/mission-and-vision" },
      { translation_key: "header.second-sub-menu-3", url: "/about/core-values" },
      { translation_key: "header.second-sub-menu-4", url: "/about/directorates" },
      { translation_key: "header.second-sub-menu-5", url: "/about/regulations&policies" },
      { translation_key: "header.second-sub-menu-6", url: "/about/machines" },
      { translation_key: "header.second-sub-menu-7", url: "/about/clients" },
    ],
  },
  { translation_key: "header.third-menu", url: "/agccServices", icon: "GrServices", children: [] },
  { translation_key: "header.fourth-menu", url: "/agccProjects", icon: "GrProjects", children: [] },
  { translation_key: "header.fifth-menu", url: "/agccNews", icon: "GoFileMedia", children: [] },
  {
    translation_key: "header.sixth-menu",
    url: "/gallery/photos",
    icon: "TfiGallery",
    children: [
      { translation_key: "header.sixth-sub-menu-1", url: "/gallery/photos" },
      { translation_key: "header.sixth-sub-menu-2", url: "/gallery/videos" },
    ],
  },
  { translation_key: "header.seventh-menu", url: "/contact-us", icon: "RiContactsBookLine", children: [] },
];

const Navbar = () => {
  const [navbarShow, setNavbarShow] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const { setting, menu } = useSite();

  const languages = [
    { code: "per", name: "دری | ", dir: "rtl", country_code: "ir" },
    { code: "ps", name: "پښتو", dir: "rtl", country_code: "pk" },
    { code: "en", name: "| English", country_code: "gb" },
  ];

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [topOffset, setTopOffset] = useState(40);

  const currentLanguageCode = cookies.get("i18next") || "en";
  const currentLanguage = languages.find((l) => l.code === currentLanguageCode) || languages[2];
  const { t } = useTranslation();

  // Menu items come from the admin panel when available.
  const items = useMemo(() => {
    const fromApi = menu("header");
    return fromApi && fromApi.length > 0 ? fromApi : FALLBACK_MENU;
  }, [menu]);

  /** A menu item's text: its own label, or the translation key it points at. */
  const labelOf = (item) =>
    item.translation_key ? t(item.translation_key) : item.label || "";

  useEffect(() => {
    document.body.dir = currentLanguage.dir || "ltr";
    if (cookies.get("i18next") === "ps" || cookies.get("i18next") === "per") {
      document.body.className = "persian-font";
    } else {
      document.body.className = "english-font";
    }
  }, [currentLanguage, t]);

  const handleLanguageChange = (code) => {
    i18next.changeLanguage(code);
    localStorage.setItem("AGCC", code);
    setDropdownOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 700) {
        setTopOffset(0);
      } else if (window.innerWidth < 640) {
        setTopOffset(75);
      } else {
        setTopOffset(40);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const socials = [
    { key: "social_youtube", Icon: TbBrandYoutube, fallback: "https://www.youtube.com/" },
    { key: "social_facebook", Icon: FiFacebook, fallback: "https://www.facebook.com/profile.php?id=100038284699429" },
    { key: "social_twitter", Icon: BsTwitterX, fallback: "https://twitter.com/" },
    { key: "social_instagram", Icon: FiInstagram, fallback: "https://www.instagram.com/" },
    {
      key: "social_linkedin",
      Icon: FiLinkedin,
      fallback: "https://www.linkedin.com/in/areyana-group-zabet-khan-60198b209",
    },
  ];

  const submitSearch = (event) => {
    event.preventDefault();
    if (query.trim().length >= 2) setSearchOpen(true);
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-40 h-[80px] bg-gray-200  ">
        <div className="mx-2 sm:mx-20 flex   sm:justify-between items-center ">
          <div className="flex flex-col sm:flex-row items-start sm:items-center text-[12px] sm:text-md mt-1 text-gray-700 ">
            <a
              href={`tel:${setting("contact_phone_raw", "+93777757731")}`}
              className="flex items-center rtl:flex-row-reverse  gap-1 sm:gap-2"
            >
              <FaPhoneAlt className="flex-shrink-0 text-mstheme" />
              <span className="unicode-bidi[override] rtl:text-left text-[8px] sm:text-sm">
                {setting("contact_phone", t("visitorInfo-phone"))}
              </span>
            </a>
            <a
              href={`mailto:${setting("contact_email", "info@areyanagroup.com")}`}
              className="flex items-center rtl:flex-row-reverse gap-1 mx-2 sm:gap-2 mt-1 sm:mt-0  text-[8px] sm:text-sm"
            >
              <FaEnvelope className="flex-shrink-0 text-mstheme" />
              <span>{setting("contact_email", "info@areyanagroup.com")}</span>
            </a>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setQuoteOpen(true)}
              className="bg-mstheme text-white px-4 rounded-tr-xl rounded-br-sm rounded-bl-xl rounded-tl-sm"
            >
              {t("quote.button", "Get A Quote")}
            </button>
          </div>

          <div className="flex  text-gray-700">
            <div
              className=" text-[8px] sm:text-sm sm:font-semibold sm:px-4 mt-2 "
              dangerouslySetInnerHTML={{ __html: t("top-header.follow") }}
            />
            <div className="flex p-2 ">
              {socials
                .filter(({ key, fallback }) => setting(key, fallback))
                .map(({ key, Icon, fallback }) => (
                  <a
                    key={key}
                    href={setting(key, fallback)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={key.replace("social_", "")}
                    className="text-[10px] sm:text-xl mx-1 sm:mx-3  text-mstheme rounded"
                  >
                    <Icon className="hover:scale-110  transition-transform duration-300 " />
                  </a>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{ top: `${topOffset}px` }}
        className="fixed left-0 right-0 z-40 transition-all duration-1000"
      >
        <div className="navbar  bg-gray-200 text-mstheme flex items-center justify-between px-4 ">
          {/* Logo Section */}
          <div className="navbar-start flex items-center w-full">
            <Link to="/" className="flex items-center text-lg font-bold   rounded gap-1">
              <img
                src={setting("site_logo", "") || mlogo}
                alt={setting("site_name", "Areyana Group of Construction Company")}
                className="h-6 md:h-12 ml-0  md:ml-12 w-auto mr-2"
              />
            </Link>
          </div>

          {setting("show_search_box", "1") !== "0" && (
            <form onSubmit={submitSearch} className="hidden lg:flex">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-[600px] border-mstheme outline-none border-2 rounded px-4 py-2 "
                placeholder={t("search.placeholder", "Search on Areyana Construction Company")}
              />
              <button type="submit" aria-label="Search">
                <FaSearch className="text-2xl text-mstheme relative right-10 " />
              </button>
            </form>
          )}

          {/* Language Dropdown Section */}
          <div className="navbar-end flex items-center gap-2 relative me-28">
            <div
              className="flex items-center gap-2 relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <div>
                <div className="p-2 shadow-lg bg-mstheme text-fontcolor rounded-box flex">
                  {languages
                    .filter((language) => language.code !== currentLanguageCode)
                    .map((language) => (
                      <div key={language.code}>
                        <button
                          className="w-full text-left px-2 hover:bg-mstheme_hover hover:text-hover_color rounded focus:bg-mstheme_hover focus:text-white focus:outline-none"
                          onClick={() => handleLanguageChange(language.code)}
                        >
                          {language.name}
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Mobile Hamburger Menu */}
            <button
              type="button"
              aria-label="Menu"
              className="flex sm:hidden transition-all duration-700"
              onClick={() => setNavbarShow(!navbarShow)}
            >
              {navbarShow ? <RxCross2 /> : <GiHamburgerMenu />}
            </button>
          </div>
        </div>

        <div className="bg-gray-50 pt-5 pb-2 flex items-center justify-center">
          {/* Desktop menu */}
          <div className="navbar-center hidden lg:flex">
            <ul className="menu menu-horizontal space-x-5 ml-24">
              {items.map((item, index) => {
                const Icon = ICONS[item.icon];
                const hasChildren = (item.children || []).length > 0;

                if (!hasChildren) {
                  return (
                    <li key={item.id || `${item.url}-${index}`}>
                      <Link
                        to={item.url || "/"}
                        target={item.target || "_self"}
                        className="text-[16px] font-medium hover:bg-mstheme_hover hover:text-hover_color px-2  rounded focus:bg-mstheme_hover focus:text-white focus:outline-none"
                      >
                        {Icon && <Icon />} {labelOf(item)}
                      </Link>
                    </li>
                  );
                }

                return (
                  <li key={item.id || `${item.url}-${index}`} className="dropdown dropdown-hover">
                    <label
                      tabIndex={0}
                      className="text-[16px] font-medium cursor-pointer flex items-center hover:bg-white hover:text-mstheme px-2  rounded focus:bg-mstheme_hover focus:text-white focus:outline-none"
                    >
                      {Icon && <Icon />} {labelOf(item)} <FaChevronDown className="ml-1" />
                    </label>
                    <ul
                      tabIndex={0}
                      className="dropdown-content p-2 shadow bg-mstheme text-fontcolor rounded-box w-44 sm:w-64 mt-1 z-20"
                    >
                      {item.children.map((child, childIndex) => (
                        <li key={child.id || `${child.url}-${childIndex}`}>
                          <Link
                            to={child.url || "/"}
                            className="hover:bg-white hover:text-mstheme px-2  rounded focus:bg-mstheme_hover focus:text-white focus:outline-none"
                          >
                            <WiDirectionRight size={30} /> {labelOf(child)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Mobile menu */}
          {navbarShow && (
            <div className="navbar-center flex flex-col lg:hidden ">
              <ul className="absolute bg-mstheme top-14 menu  space-x-5 ml-24 z-30">
                {items.map((item, index) => {
                  const hasChildren = (item.children || []).length > 0;

                  if (!hasChildren) {
                    return (
                      <li key={item.id || `m-${item.url}-${index}`}>
                        <Link
                          to={item.url || "/"}
                          onClick={() => setNavbarShow(false)}
                          className="text-[16px] font-medium hover:bg-mstheme_hover hover:text-hover_color px-2  rounded focus:bg-mstheme_hover focus:text-white focus:outline-none"
                        >
                          {labelOf(item)}
                        </Link>
                      </li>
                    );
                  }

                  return (
                    <li key={item.id || `m-${item.url}-${index}`} className="dropdown dropdown-hover">
                      <label
                        tabIndex={0}
                        className="text-[16px] font-medium cursor-pointer flex items-center hover:bg-mstheme_hover hover:text-hover_color px-2  rounded focus:bg-mstheme_hover focus:text-white focus:outline-none"
                      >
                        {labelOf(item)} <FaChevronDown className="ml-1" />
                      </label>
                      <ul
                        tabIndex={0}
                        className="dropdown-content p-2 shadow bg-mstheme rounded-box w-40 mt-1 z-20"
                      >
                        {item.children.map((child, childIndex) => (
                          <li key={child.id || `m-${child.url}-${childIndex}`}>
                            <Link
                              to={child.url || "/"}
                              onClick={() => setNavbarShow(false)}
                              className="hover:bg-mstheme_hover hover:text-hover_color px-2  rounded focus:bg-mstheme_hover focus:text-white focus:outline-none"
                            >
                              {labelOf(child)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      <a
        href={`https://wa.me/${setting("whatsapp_number", "93777757731")}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        className="fixed right-5 bottom-20 md:bottom-28 z-50 rounded-full"
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/5/5e/WhatsApp_icon.png"
          alt="Whatsapp icon"
          className=" w-11 md:w-16"
        />
      </a>

      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      <SearchResults open={searchOpen} query={query} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Navbar;
